import { useState, useRef, useEffect } from 'react';

import styled, { keyframes } from 'styled-components';

import { useTheme } from '../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../Styles/theme';
import { useLanguage } from '../../../Context/LanguageContext';

import CustomScrollbar from '../../Share/Normal/CustomScrollbar';


const COL_WIDTH = 38;
const Y_AXIS_WIDTH = 34;
const PLOT_HEIGHT = 250;

export default function MemberStatusBarChart(props) {
  const {
    monthlyStats,
    newColor,
    renewalColor,
    churnColor,
    scrollBarColor,
  } = props;

  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;
  const { t } = useLanguage();

  const [hoveredIndex, setHoveredIndex] = useState(-1);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [viewWidth, setViewWidth] = useState(0);

  const ScrollRef = useRef();

  // 스크롤 뷰포트 실제 너비 추적 (툴팁 위치 클램프용) //
  useEffect(() => {
    const el = ScrollRef.current;

    if (!el) return;

    const update = () => setViewWidth(el.clientWidth);

    update();

    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(el);

    return () => resizeObserver.disconnect();
  }, []);

  // 세로 휠 스크롤을 가로 스크롤로 변환 //
  useEffect(() => {
    const el = ScrollRef.current;

    if (!el) return;

    const onWheel = (e) => {
      // 가로 스크롤 여지가 없으면 기본 동작 유지 //
      if (el.scrollWidth <= el.clientWidth) return;

      // 세로 델타가 더 큰 경우(일반 마우스 휠)만 가로로 전환 //
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;

      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };

    el.addEventListener('wheel', onWheel, { passive: false });

    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  if (monthlyStats.length === 0) return null;

  // 상단/하단 최대값 산정 //
  let topMax = 0;
  let bottomMax = 0;

  for (let i = 0; i < monthlyStats.length; i++) {
    const topValue = monthlyStats[i].new + monthlyStats[i].renewal;

    if (topValue > topMax) topMax = topValue;
    if (monthlyStats[i].churn > bottomMax) bottomMax = monthlyStats[i].churn;
  }

  // 눈금 간격 계산 //
  function calcStep(maxValue) {
    if (maxValue <= 5) return 1;
    if (maxValue <= 10) return 2;
    if (maxValue <= 30) return 5;
    if (maxValue <= 60) return 10;
    if (maxValue <= 150) return 25;
    return 50;
  }

  const step = calcStep(Math.max(topMax, bottomMax));
  const topMaxRounded = Math.max(Math.ceil(topMax / step) * step, step);
  const bottomMaxRounded = Math.ceil(bottomMax / step) * step;

  const unitPx = PLOT_HEIGHT / (topMaxRounded + bottomMaxRounded);
  const zeroLine = topMaxRounded * unitPx;
  const zeroFromBottom = bottomMaxRounded * unitPx;

  // 눈금(그리드 + Y축 라벨) 목록 생성 //
  const ticks = [];

  for (let v = topMaxRounded; v >= -bottomMaxRounded; v -= step) {
    ticks.push({ value: v, y: (topMaxRounded - v) * unitPx });
  }

  // 가로 스크롤 위치 추적 //
  function onScrollBars() {
    if (ScrollRef.current) setScrollLeft(ScrollRef.current.scrollLeft);
  }

  // 마우스 위치로 호버 컬럼 인덱스 산정 //
  function onMouseMoveColumns(e) {
    const rect = e.currentTarget.getBoundingClientRect();
    const index = Math.floor((e.clientX - rect.left) / COL_WIDTH);

    if (index >= 0 && index < monthlyStats.length) setHoveredIndex(index);
  }

  // Y축 눈금 라벨 렌더링 //
  function renderYAxis() {
    return (
      <YAxisColumn>
        {
          ticks.map((tick, index) => (
            <YAxisLabel key={'MemberStatusYAxis-' + index} $top={tick.y}>
              <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.subText}>
                {Math.abs(tick.value)}
              </Text>
            </YAxisLabel>
          ))
        }
      </YAxisColumn>
    );
  }

  // 배경 그리드 라인 렌더링 //
  function renderGridLines() {
    return (
      <GridLayer>
        {
          ticks.map((tick, index) => (
            <GridLine key={'MemberStatusGrid-' + index} $top={tick.y} $isZero={tick.value === 0} />
          ))
        }
      </GridLayer>
    );
  }

  // 월별 막대 컬럼 렌더링 //
  function renderColumns() {
    return monthlyStats.map((data, index) => {
      const newHeight = data.new * unitPx;
      const renewalHeight = data.renewal * unitPx;
      const churnHeight = data.churn * unitPx;

      const isRenewalTop = data.renewal > 0;

      return (
        <BarColumn key={'MemberStatusColumn-' + index}>
          {
            renewalHeight > 0 &&
            <UpBar
              $color={renewalColor}
              $bottom={zeroFromBottom + newHeight}
              $height={renewalHeight}
              $roundTop={true} />
          }
          {
            newHeight > 0 &&
            <UpBar
              $color={newColor}
              $bottom={zeroFromBottom}
              $height={newHeight}
              $roundTop={!isRenewalTop} />
          }
          {
            churnHeight > 0 &&
            <DownBar
              $color={churnColor}
              $top={zeroLine}
              $height={churnHeight} />
          }
        </BarColumn>
      );
    });
  }

  // 월 라벨 렌더링 (현재 월은 NOW 표기) //
  function renderMonthLabels() {
    return monthlyStats.map((data, index) => (
      <MonthLabelCell key={'MemberStatusMonthLabel-' + index}>
        {
          data.isNow &&
          <NowBadge $color={newColor}>
            <Text fontFamily='NotoSansKR-Medium' fontSize={8} color={theme.subBackground}>NOW</Text>
          </NowBadge>
        }
        <Text fontFamily='NotoSansKR-Medium' fontSize={13} color={data.isNow ? newColor : theme.subText}>
          {data.label}
        </Text>
      </MonthLabelCell>
    ));
  }

  // 호버 툴팁 렌더링 //
  function renderTooltip() {
    if (hoveredIndex === -1) return null;

    const data = monthlyStats[hoveredIndex];
    const rawLeft = Y_AXIS_WIDTH + (hoveredIndex * COL_WIDTH) + (COL_WIDTH / 2) - scrollLeft;
    const maxLeft = Y_AXIS_WIDTH + viewWidth - 130;
    const tooltipLeft = Math.max(Y_AXIS_WIDTH, Math.min(rawLeft - 65, maxLeft));

    return (
      <TooltipBox $left={tooltipLeft}>
        <TooltipTitle>
          <Text fontFamily='NotoSansKR-Medium' fontSize={16} color={theme.text}>{data.label}</Text>
        </TooltipTitle>
        <TooltipRow>
          <TooltipLabelWrapper>
            <ColorDot $color={newColor} />
            <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.subText}>{t('statistics.memberStatus.tabNew')}</Text>
          </TooltipLabelWrapper>
          <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={newColor}>{'+' + data.new}</Text>
        </TooltipRow>
        <TooltipRow>
          <TooltipLabelWrapper>
            <ColorDot $color={renewalColor} />
            <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.subText}>{t('statistics.memberStatus.tabReRegistered')}</Text>
          </TooltipLabelWrapper>
          <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={renewalColor}>{'+' + data.renewal}</Text>
        </TooltipRow>
        <TooltipRow>
          <TooltipLabelWrapper>
            <ColorDot $color={churnColor} />
            <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.subText}>{t('statistics.memberStatus.tabChurned')}</Text>
          </TooltipLabelWrapper>
          <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={churnColor}>{'-' + data.churn}</Text>
        </TooltipRow>
        <TooltipRow>
          <TooltipLabelWrapper>
            <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.subText}>{t('statistics.memberStatus.net')}</Text>
          </TooltipLabelWrapper>
          <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>{(data.net >= 0 ? '+' : '') + data.net}</Text>
        </TooltipRow>
      </TooltipBox>
    );
  }

  const contentWidth = monthlyStats.length * COL_WIDTH;

  return (
    <TopLevelWrapper>
      <ChartRow>
        {renderYAxis()}
        <PlotColumn>
          <ScrollArea ref={ScrollRef} onScroll={onScrollBars}>
            <BarsContent $width={contentWidth}>
              {renderGridLines()}
              <ColumnsRow
                onMouseMove={onMouseMoveColumns}
                onMouseLeave={() => setHoveredIndex(-1)}>
                {renderColumns()}
              </ColumnsRow>
              <MonthLabelRow>
                {renderMonthLabels()}
              </MonthLabelRow>
            </BarsContent>
          </ScrollArea>
          <CustomScrollbar
            key={monthlyStats.length}
            direction="horizontal"
            scrollRef={ScrollRef}
            thumbSize="auto"
            thumbColor={theme.gray}
            showTrackAlways={false}
            trackOffset={10} />
        </PlotColumn>
      </ChartRow>
      {renderTooltip()}
    </TopLevelWrapper>
  );
};

const growUp = keyframes`
  from { transform: scaleY(0); }
  to { transform: scaleY(1); }
`;

const growDown = keyframes`
  from { transform: scaleY(0); }
  to { transform: scaleY(1); }
`;

const TopLevelWrapper = styled.div`
  position: relative;

  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;

  padding-top: 20px;

  flex: 1;
`;

const ChartRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: flex-start;

  width: 100%;
`;

const YAxisColumn = styled.div`
  position: relative;

  width: ${Y_AXIS_WIDTH}px;
  height: ${PLOT_HEIGHT}px;

  flex-shrink: 0;
`;

const YAxisLabel = styled.div`
  position: absolute;
  top: ${props => props.$top}px;
  left: 0;

  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  width: 100%;

  transform: translateY(-50%);
`;

const PlotColumn = styled.div`
  display: flex;
  flex-direction: column;

  flex: 1;
  min-width: 0;
`;

const ScrollArea = styled.div`
  display: flex;
  flex-direction: row;

  width: 100%;

  overflow-x: auto;
  overflow-y: hidden;

  /* 네이티브 스크롤바 숨김 (CustomScrollbar로 대체) */
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const BarsContent = styled.div`
  position: relative;

  display: flex;
  flex-direction: column;

  width: ${props => props.$width}px;

  padding-bottom: 17px;

  flex-shrink: 0;
`;

const GridLayer = styled.div`
  position: absolute;
  top: 0;
  left: 0;

  width: 100%;
  height: ${PLOT_HEIGHT}px;
`;

const GridLine = styled.div`
  position: absolute;
  top: ${props => props.$top}px;
  left: 0;

  width: 100%;
  height: 1px;

  background-color: ${props => props.theme.line};
  opacity: ${props => props.$isZero ? 1 : 0.5};
`;

const ColumnsRow = styled.div`
  position: relative;

  display: flex;
  flex-direction: row;

  height: ${PLOT_HEIGHT}px;
`;

const BarColumn = styled.div`
  position: relative;

  width: ${COL_WIDTH}px;
  height: ${PLOT_HEIGHT}px;

  flex-shrink: 0;

  cursor: pointer;
`;

const UpBar = styled.div`
  position: absolute;
  bottom: ${props => props.$bottom}px;
  left: 6px;

  width: 20px;
  height: ${props => props.$height}px;

  border-radius: ${props => props.$roundTop ? '2px 2px 0 0' : '0'};
  background-color: ${props => props.$color};

  transform-origin: bottom;
  animation: ${growUp} 0.6s ease-in-out;
`;

const DownBar = styled.div`
  position: absolute;
  top: ${props => props.$top}px;
  left: 6px;

  width: 20px;
  height: ${props => props.$height}px;

  border-radius: 0 0 2px 2px;
  background-color: ${props => props.$color};

  transform-origin: top;
  animation: ${growDown} 0.6s ease-in-out;
`;

const MonthLabelRow = styled.div`
  display: flex;
  flex-direction: row;

  width: 100%;

  margin-top: 24.5px;
`;

const MonthLabelCell = styled.div`
  position: relative;

  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: center;

  width: ${COL_WIDTH}px;

  flex-shrink: 0;
`;

const NowBadge = styled.div`
  position: absolute;
  bottom: calc(100% + 4px);
  left: 50%;
  transform: translateX(-50%);

  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  padding: 1px 4px;

  border-radius: 3px;
  background-color: ${props => props.$color};

  white-space: nowrap;
`;

const TooltipBox = styled.div`
  position: absolute;
  top: 0;
  left: ${props => props.$left}px;

  transition: left 0.15s ease-out;

  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  padding: 10px;

  gap: 1px;

  width: 130px;

  border-radius: 5px;
  background-color: ${props => props.theme.background};
  box-shadow: 0px 0px 30px 0px rgba(0, 0, 0, 0.1);

  box-sizing: border-box;

  z-index: 5;
  pointer-events: none;
`;

const TooltipTitle = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  padding: 2px 0;

  width: 100%;

  border-radius: 2px;
  background-color: ${props => props.theme.buttonFill};
`;

const TooltipRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  width: 100%;
`;

const TooltipLabelWrapper = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  gap: 10px;
`;

const ColorDot = styled.div`
  width: 17px;
  height: 17px;

  border-radius: 2px;
  background-color: ${props => props.$color};
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0 0 0 0;

  white-space: nowrap;
`;
